/**
 * Which URLs a band's SocialMediaLinks may hold.
 *
 * The host allowlist is `config/socialMediaAllowlist.json`. It is the
 * frontend's own copy of the list the backend validator enforces (ENG-357,
 * in the backend repo) - the backend cannot embed a file from this repo, so
 * the two are kept in step by hand. The backend is authoritative: this copy
 * only decides what the forms block before the round trip and what the pages
 * render, so when the lists differ the safe direction is for this one to be
 * at least as broad. A host missing here but accepted there hides a link the
 * backend stored, whereas a host listed here but rejected there just means
 * the member sees the backend's own error on save.
 *
 * The JSON is the whole policy - there are no host rules in code:
 *   - `host`   matches that host exactly (after lowercasing). Apex domains are
 *              listed with their `www.` form and the mobile / short-link
 *              hosts people actually paste (m.facebook.com, on.soundcloud.com),
 *              plus subdomains that *are* the platform (open.spotify.com,
 *              music.apple.com). Parents and siblings of those are not
 *              implied: spotify.com and play.spotify.com are not listed.
 *   - `*.host` matches any subdomain of host on a dot boundary, for platforms
 *              that give every band its own (`<band>.bandcamp.com`). It does
 *              not match host itself and, like urlPath in streamSource.js,
 *              `evilbandcamp.com` never passes.
 *
 * The URL check approximates the backend's: https only, no userinfo, no
 * explicit port, host lowercased and matched against the list. It is the
 * browser's WHATWG parser rather than Go's net/url, so the two disagree at the
 * edges (a backslash in the authority, a trailing dot on the host) - harmless,
 * since the backend still validates every write. The parser already lowercases
 * ASCII hosts and punycode-encodes IDN ones, so a homoglyph host comes out as
 * `xn--…` and simply never matches.
 *
 * Used both before a form submits (BandSettings, RegisterBand) and at render
 * time (BandInfo, PerformerHome): the backend validator only guards new
 * writes, so any link stored before it landed - including a `javascript:`
 * one that parses fine - must still be kept out of an href here.
 */
import SOCIAL_MEDIA_ALLOWLIST from '../config/socialMediaAllowlist.json';
import { englishT } from '../locales/index.js';

export { SOCIAL_MEDIA_ALLOWLIST };

/** Exact hosts, and the parents whose `*.` entry admits every subdomain. */
const EXACT_HOSTS = new Set(SOCIAL_MEDIA_ALLOWLIST.filter((entry) => !entry.startsWith('*.')));
const WILDCARD_PARENTS = SOCIAL_MEDIA_ALLOWLIST.filter((entry) => entry.startsWith('*.')).map((entry) => entry.slice(2));

/** True when `host` (already lowercased) is admitted by the allowlist. */
const isAllowedHost = (host) =>
  EXACT_HOSTS.has(host) || WILDCARD_PARENTS.some((parent) => host.endsWith(`.${parent}`));

/** Anything that parses as `scheme:`; a value starting like this is left alone. */
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * The form of `link` to validate, store and render: trimmed, and given the
 * `https://` most people leave off when they paste `instagram.com/theband`
 * from memory (extractTwitchChannel in bandUtils already tolerates such rows).
 * A value that already carries a scheme - including `http:` or `javascript:` -
 * is returned as is, so this never turns a rejected link into an accepted one.
 * Non-strings and blanks come back as ''.
 */
export const normalizeSocialMediaUrl = (link) => {
  const trimmed = typeof link === 'string' ? link.trim() : '';
  if (trimmed === '' || HAS_SCHEME.test(trimmed)) return trimmed;
  return trimmed.startsWith('//') ? `https:${trimmed}` : `https://${trimmed}`;
};

/**
 * True when `url` is an https link to a host on the allowlist. Never throws:
 * a malformed value (legacy data, half-typed input) is simply not allowed.
 * Takes the value as given - callers that want `instagram.com/theband` to
 * count pass it through normalizeSocialMediaUrl first.
 */
export const isAllowedSocialMediaUrl = (url) => {
  if (typeof url !== 'string' || url === '') return false;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  if (parsed.username !== '' || parsed.password !== '') return false;
  if (parsed.port !== '') return false;
  const host = parsed.hostname.toLowerCase();
  return host !== '' && isAllowedHost(host);
};

/**
 * The hrefs a stored SocialMediaLinks value may render as: each entry
 * normalized, keeping only those that pass isAllowedSocialMediaUrl, in order.
 * Tolerates a missing or non-array value (older API shapes hand back null).
 */
export const allowedSocialMediaLinks = (links) =>
  Array.isArray(links) ? links.map(normalizeSocialMediaUrl).filter(isAllowedSocialMediaUrl) : [];

/**
 * How many non-empty entries of a stored value allowedSocialMediaLinks drops,
 * so a page can tell the member that something they saved is not being shown.
 */
export const countHiddenSocialMediaLinks = (links) => {
  if (!Array.isArray(links)) return 0;
  const present = links.map(normalizeSocialMediaUrl).filter((link) => link !== '');
  return present.length - present.filter(isAllowedSocialMediaUrl).length;
};

/**
 * Short label for an allowed link: its host without a leading `www.`
 * (`instagram.com`, `theband.bandcamp.com`). Only meaningful for a link that
 * passed isAllowedSocialMediaUrl; anything else falls back to the raw value
 * rather than throwing.
 */
export const socialMediaLinkLabel = (link) => {
  try {
    return new URL(link).hostname.replace(/^www\./, '');
  } catch {
    return String(link ?? '');
  }
};

/**
 * What to show when a form holds a link that will not be accepted, in the
 * given locale (English without a `t`). The message names the platforms that
 * do work so the member knows what to paste instead.
 */
export const socialMediaLinkError = (link, t = englishT) => t('common.socialLinks.unsupportedLink', { link });

/**
 * The submit-time check both band forms run: the first non-empty link that is
 * not allowed once normalized, as an error message quoting it as typed
 * (trimmed), or null when every link may be sent. Pass the component's `t`
 * for the active locale; without one the message is English.
 */
export const findSocialMediaLinkError = (links, t = englishT) => {
  for (const link of links) {
    const typed = typeof link === 'string' ? link.trim() : '';
    if (typed !== '' && !isAllowedSocialMediaUrl(normalizeSocialMediaUrl(typed))) return socialMediaLinkError(typed, t);
  }
  return null;
};
